import { useEffect, useState, useCallback } from 'react';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  isVerified: boolean;
  isAlphyAdmin: boolean;
  avatarUrl?: string;
  lastLoginAt?: string;
  createdAt?: string;
  canCreateApiKeys?: boolean;
}

interface MeResponse { user: AuthUser }

interface LoginResponse {
  token: string;
  user: { id: string; email: string; name: string; isAlphyAdmin: boolean };
}

interface ProvidersResponse { google: boolean; microsoft: boolean }

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    let body: { error?: { message?: string }; message?: string } = {};
    try { body = await res.json(); } catch { /* ignore */ }
    const msg = body?.error?.message || body?.message || `Request failed (${res.status})`;
    const err = new Error(msg) as Error & { status?: number; code?: string };
    err.status = res.status;
    err.code = (body as { error?: { code?: string } })?.error?.code;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const authApi = {
  me: () => fetchJson<MeResponse>('/api/auth/me'),
  login: (email: string, password: string) =>
    fetchJson<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => fetchJson<{ message: string }>('/api/auth/logout', { method: 'POST' }),
  providers: () => fetchJson<ProvidersResponse>('/api/auth/providers'),
};

// SSO redirect helpers — full-page navigation, not fetch.
export function googleSignInUrl() { return '/api/auth/google?return_to=console'; }
export function microsoftSignInUrl() { return '/api/auth/microsoft?return_to=console'; }

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

/**
 * useAuth — load /auth/me on mount. The hc_token cookie is sent automatically
 * (httpOnly, same-origin). Returns { user, loading, error, refresh, signOut }.
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>({ user: null, loading: true, error: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { user } = await authApi.me();
      setState({ user, loading: false, error: null });
    } catch (err) {
      const e = err as Error & { status?: number };
      // 401 means "not logged in" — don't surface as an error.
      setState({ user: null, loading: false, error: e.status === 401 ? null : e.message });
    }
  }, []);

  const signOut = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setState({ user: null, loading: false, error: null });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...state, refresh, signOut };
}
